/**
 * Maps Firebase Auth error codes to messages we can show customers.
 */

export type AuthErrorContext = 'signin' | 'signup' | 'reset';

const DEFAULT_MESSAGES: Record<AuthErrorContext, string> = {
  signin: 'Failed to sign in. Please try again.',
  signup: 'Failed to create account. Please try again.',
  reset: 'Could not send the reset email. Please try again.',
};

/**
 * Extract a Firebase error code (e.g. "auth/wrong-password") from an unknown
 * error, or undefined if there isn't one.
 */
export function getAuthErrorCode(error: unknown): string | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return (error as { code: string }).code;
  }
  return undefined;
}

export function getAuthErrorMessage(
  error: unknown,
  context: AuthErrorContext
): string {
  switch (getAuthErrorCode(error)) {
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Try signing in instead.';
    case 'auth/invalid-email':
      return 'That email address doesn’t look right. Please check it and try again.';
    case 'auth/weak-password':
      return 'Your password must be at least 6 characters.';
    // Treat all bad-credential variants identically so the message never
    // reveals whether an email has an account.
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
      return context === 'signin'
        ? 'Incorrect email or password. Please try again.'
        : DEFAULT_MESSAGES[context];
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Google sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Please allow popups and try again.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact hello@sundaybestapp.com for help.';
    default:
      return DEFAULT_MESSAGES[context];
  }
}
